import React, { Fragment, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  activeFilterSelector,
  areFiltersSet,
  getFiltersOpen,
  bikesSelector,
  getFeatureCardCount,
} from 'src/redux/bikes/selectors';
import {
  setActiveFilter,
  clearFilters,
  openFiltersModal,
  closeFiltersModal,
  applyFilters,
} from 'src/redux/bikes/actions';
import { filtersSelector } from 'src/redux/app/selectors';
import CloseIcon from 'src/assets/icons/close.svg';
import ResetIcon from 'src/assets/icons/reset.svg';
import FiltersIcon from 'src/assets/icons/filters.svg';
import Label from 'components/_utility/Label';
import { bodyNoScroll } from 'helpers/utilities';
import usePrevious from 'src/hooks/usePrevious';
import {
  Wrap,
  FilterCards,
  TitleBar,
  CloseButton,
  ApplyButtonWrapper,
  ApplyButton,
  ResultsBar,
  ResultsCount,
  ResetButton,
  RefineResults,
  RefineResultsInner,
  FilterButton,
  ActiveFiltersOuter,
  FiltersBody,
} from './BikeFilters.styled';
import FilterCard from './FilterCard';
import ActiveFilters from './ActiveFilters';
import ViewAndSort from './ViewAndSort';

const BikeFilters = ({ fluid, viewHidden = false, isFamilyPage = false, isGenrePage = false }) => {
  const dispatch = useDispatch();
  const filters = useSelector(filtersSelector);
  const openFilterIndex = useSelector(activeFilterSelector);
  const filtersSet = useSelector(areFiltersSet);
  const filtersOpen = useSelector(getFiltersOpen);
  const { filteredCount: bikeCardCount } = useSelector(bikesSelector);
  const featureCardCount = useSelector(getFeatureCardCount(isFamilyPage ? 'family' : 'genre'));
  const previousFiltersOpen = usePrevious(filtersOpen);
  const count = isFamilyPage || isGenrePage ? featureCardCount : bikeCardCount;

  const handleSelectCard = filterId => dispatch(setActiveFilter(filterId));

  const filterOpen = typeof openFilterIndex === 'number';

  useEffect(() => {
    if (filtersOpen) {
      bodyNoScroll.add();
    }

    if (!filtersOpen && previousFiltersOpen) {
      bodyNoScroll.remove();
    }
  }, [filtersOpen, previousFiltersOpen]);

  useEffect(() => {
    const closeFiltersOnOrientationChange = () => {
      if (filtersOpen) {
        handleSelectCard(null);
        bodyNoScroll.remove();
      }
    };

    window.addEventListener('resize', closeFiltersOnOrientationChange);

    return () => window.removeEventListener('resize', closeFiltersOnOrientationChange);
  });

  return (
    <Fragment>
      <Wrap visible={filtersOpen} filterOpen={filterOpen}>
        <TitleBar>
          <Label name="filtersLabel" />
          <CloseButton data-testid="closeFiltersBtn" onClick={() => dispatch(closeFiltersModal())}>
            <CloseIcon />
          </CloseButton>
        </TitleBar>
        <FiltersBody>
          <FilterCards>
            {filters.map((filter, index) => {
              const active = index === openFilterIndex;

              return (
                <FilterCard {...filter} key={index} index={index} onSelectCard={handleSelectCard} active={active} />
              ); // Used index as key here, as confident order of this array won't change
            })}
          </FilterCards>
          <ActiveFiltersOuter>
            <ActiveFilters fluid={fluid} />
          </ActiveFiltersOuter>
          {filtersSet ? (
            <ResultsBar>
              <ResultsCount>
                <strong>{count}</strong> <Label name="resultsLabel" />
              </ResultsCount>
              <ResetButton data-testid="clearFiltersBtn" onClick={() => dispatch(clearFilters())}>
                <ResetIcon></ResetIcon>
                <Label name="clearFiltersLabel" />
              </ResetButton>
            </ResultsBar>
          ) : null}
        </FiltersBody>
        <ApplyButtonWrapper>
          <ApplyButton onClick={() => dispatch(applyFilters())}>
            <Label name="applyFiltersLabel" />
          </ApplyButton>
        </ApplyButtonWrapper>
      </Wrap>
      <RefineResults filterOpen={filterOpen}>
        <RefineResultsInner fluid={fluid}>
          <ActiveFiltersOuter hideOnMobile>
            <ActiveFilters fluid={fluid} />
          </ActiveFiltersOuter>
          <FilterButton onClick={() => dispatch(openFiltersModal())} data-testid="openFiltersBtn">
            <Label name="filtersLabel" />
            <FiltersIcon></FiltersIcon>
          </FilterButton>
          <ViewAndSort viewHidden={viewHidden}></ViewAndSort>
        </RefineResultsInner>
      </RefineResults>
    </Fragment>
  );
};

export default BikeFilters;
